import { ProjectSectionListItem } from '@modules/projects/components/Sections/ProjectSectionList';
import { ListItemButton, ListItemProps, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import dayjs from 'dayjs';
import { ApplicationUser, FormDefinition, FormSettings, ProjectForm, ProjectTaskFragmentFragment, ProjectTaskStatus, useRequestFormSubmissionMutation } from 'gql/index';
import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useTaskFormatters } from '../useTaskFormatters';

type FormListItemProps = ListItemProps & {
  task: ProjectTaskFragmentFragment,
  form: Pick<ProjectForm, 'id' | 'publishedOn' | 'createdOn' | 'status'> & {
    allowedUsers: Pick<ApplicationUser, 'id'>[];
    formDefinition?: Pick<FormDefinition, 'name'> | null;
    formSettings?: Pick<FormSettings, 'formType'>;
  },
  onOpenTask?: (task: ProjectTaskFragmentFragment, projectId: number) => void;
  secondaryAction?: React.ReactNode;
  assigneesAdornment?: React.ReactNode;
};

export const FormTaskListItem: React.FC<FormListItemProps> = ({ form, task, onOpenTask, secondaryAction, assigneesAdornment, ...listItemProps }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { formatTaskSecondaryText } = useTaskFormatters();
  const isOverdue = task.status !== ProjectTaskStatus.Done && (task.dueDate ? dayjs(task.dueDate).isBefore(dayjs(), 'day') : false);
  const secondaryText = formatTaskSecondaryText(task);

  const { mutate: requestSubmission, isPending: isRequestingSubmission } = useRequestFormSubmissionMutation();

  const onRequestSubmissionClicked = () => {
    requestSubmission({ input: { formId: form.id } }, {
      onSuccess: d => {
        const id = d.requestFormSubmission.projectFormSubmission?.id;
        if (!id) return;
        navigate(`/${getTenantIdentifier()}/projects/${task.projectId}/form/${form.id}/${id.toString()}`);
      }
    });
  };


  return (
    <ProjectSectionListItem
      disablePadding
      {...listItemProps}
      secondaryAction={(onOpenTask && task.projectId != null) || secondaryAction ? (
        <Stack direction='row' alignItems='center' spacing={1}>
          {secondaryAction}
        </Stack>
      ) : undefined}
    >
      <ListItemButton disabled={isRequestingSubmission} alignItems='center' sx={{ py: 1.5 }} onClick={onRequestSubmissionClicked}>
        <ListItemText
          primary={
            <Stack direction='row' alignItems='center' spacing={1} minWidth={0}>
              <Typography noWrap>{formatMessage({ id: 'Fill the form {formName}' }, { formName: form.formDefinition?.name })}</Typography>
              {assigneesAdornment}
            </Stack>
          }
          secondary={
            <Tooltip title={secondaryText || undefined}>
              <Typography noWrap color={isOverdue ? 'error' : 'inherit'}>{secondaryText}</Typography>
            </Tooltip>
          }
          secondaryTypographyProps={{ color: isOverdue ? 'error' : 'inherit' }}
          primaryTypographyProps={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        />
      </ListItemButton>
    </ProjectSectionListItem >
  );
};